import { IntegrationForm } from "@/components/forms/IntegrationForm";
import { getIntegrationInputQueryOptions } from "@/queries/integrations";
import { Campaign } from "@/types/campaign";
import { MaybePromise } from "@/types/maybe-promise";
import { formatExportInputs } from "@/utils/integration-helpers";
import { StepperSubmitHandler } from "@/utils/stepper";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo } from "react";
import z from "zod";

export const configureIntegrationFormSchema = z.object({
  exportInput: z.record(z.string(), z.string()),
});

export type ConfigureIntegrationFormValues = z.infer<
  typeof configureIntegrationFormSchema
>;

export function ConfigureIntegrationForm({
  exportSystem,
  exportMode,
  defaultValues,
  values,
  updateValues,
  showSubmitButton = false,
  setSubmitHandler,
  autoSubmit = false,
}: {
  exportSystem: Campaign["exportSystem"];
  exportMode: Campaign["exportMode"];
  defaultValues: ConfigureIntegrationFormValues;
  values?: ConfigureIntegrationFormValues;
  updateValues: (values: ConfigureIntegrationFormValues) => MaybePromise<void>;
  showSubmitButton?: boolean;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  autoSubmit?: boolean;
}) {
  const inputsQueryOptions = useMemo(() => {
    return getIntegrationInputQueryOptions(
      exportSystem?.toLowerCase(),
      exportMode ?? undefined,
    );
  }, [exportSystem, exportMode]);

  const { data: inputs, isFetching } = useQuery(inputsQueryOptions);

  return (
    <div className="flex flex-col">
      <AnimatePresence initial={false} mode="popLayout">
        {isFetching ? (
          <motion.div
            key="loading"
            className="flex animate-pulse items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Indlæser...
          </motion.div>
        ) : inputs ? (
          <motion.div
            key="inputs"
            className="flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <IntegrationForm
              inputs={inputs}
              defaultValues={defaultValues.exportInput}
              values={values?.exportInput}
              showSubmitButton={showSubmitButton}
              setSubmitHandler={setSubmitHandler}
              onSubmit={(data) => {
                const submissionData = formatExportInputs(data);

                return updateValues({
                  exportInput: submissionData,
                });
              }}
              autoSubmit={autoSubmit}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
